import React, { useState } from 'react';
import { DateRangePicker } from 'react-dates';
import { Button, Theme, Box } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import useShopFormat from 'hooks/useShopFormat';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useTranslation } from 'services/localization';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { LoadingData } from 'common/types';
import { ReportData } from '../types';
import { fetchReportData } from '../utils';

interface Props {
	startDate: moment.Moment | null;
	endDate: moment.Moment | null;
	shopId: string;
	onDatesChange: (dates: {
		startDate: moment.Moment | null;
		endDate: moment.Moment | null;
	}) => void;
	onReportDataChange: (data: LoadingData<ReportData>) => void;
}

export const ReportGetButton = ({
	startDate,
	endDate,
	onDatesChange,
	onReportDataChange,
	shopId,
}: Props) => {
	const { t } = useTranslation();
	const classes = useStyles();
	const { localFormatString } = useShopFormat();
	const [focusedDateInput, setFocusedDateInput] = useState<'startDate' | 'endDate' | null>(null);
	const mobileCalendar = window.matchMedia(
		'screen and (orientation:portrait) and (max-width: 640px)',
	).matches;
	const startDateNumber = useSelector(ShopSelectors.getStartDateNumber);
	const [loading, setLoading] = useState(false);

	const getReport = async () => {
		setLoading(true);
		onReportDataChange({
			loading: true,
			data: null,
			error: null,
		});
		try {
			const reportData = await fetchReportData(startDate, endDate, shopId);
			onReportDataChange({
				loading: false,
				data: reportData,
				error: null,
			});
		} catch (e) {
			onReportDataChange({
				loading: false,
				data: null,
				error: 'Error getting report. Please try again',
			});
			throw e;
		} finally {
			setLoading(false);
		}
	};

	return (
		<Box>
			<DateRangePicker
				startDate={startDate}
				startDateId="startDate"
				endDate={endDate}
				endDateId="endDate"
				onDatesChange={onDatesChange}
				focusedInput={focusedDateInput}
				onFocusChange={(focusedInput: 'startDate' | 'endDate' | null) =>
					setFocusedDateInput(focusedInput)
				}
				numberOfMonths={mobileCalendar ? 1 : 2}
				isOutsideRange={() => false}
				minimumNights={0}
				firstDayOfWeek={startDateNumber}
				displayFormat={localFormatString('DD.MM.YYYY')}
			/>
			<Button
				className={classes.reportBtn}
				variant="contained"
				color="primary"
				onClick={getReport}
				disabled={!startDate || !endDate || loading}
			>
				{t('reports.getReport', 'Get report')}
			</Button>
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		'@global': {
			'.DateRangePicker': {
				fontFamily: theme.typography.fontFamily,
			},
			'.DateRangePicker .DateInput': {
				width: 110,
			},
			'.DateRangePicker .DateInput_input': {
				fontSize: 16,
			},
			'.CalendarDay__today': {
				fontWeight: 'bold',
			},
			'@media print': {
				height: 'initial !important',
				overflow: 'initial !important',
				'-webkit-print-color-adjust': 'exact',
			},
		},
		reportBtn: {
			borderRadius: 0,
			height: 48,
		},
	}),
);

export default ReportGetButton;
